import React, { useContext, useState, useEffect, useMemo } from 'react'

// @mui core components
import {
  Box,
  Grid,
  Typography,
  Button,
  Tooltip,
  IconButton,
  Fade,
} from '@mui/material'
import Card from 'atoms/Card'
import EditIcon from '@mui/icons-material/Edit'
import HelpIcon from '@mui/icons-material/Help'

// Settings page components
import FormField from 'components/FormField'

// Data
import { IntakeContext } from 'context/IntakeContext'
import { selectData } from 'layouts/onboarding'

// Popup
import { toast } from 'react-toastify'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { useFormDebounce } from 'hooks/useFormDebounce'
import LoadingButton from 'components/LoadingButton'
import CompanySearchAutocomplete from 'components/CompanySearchAutocomplete'

function EmploymentDetails() {
  const { intakeState, updateIntakeStateAtKey, updateUser } =
    useContext(IntakeContext)

  const { updateIntakeStateAndSubmit, loading } = useFormDebounce()
  const isGovernmentEmployee = intakeState?.governmentEmployee
  // add an onSubmit button and call the context function `updateUser`
  // const onSubmit = () => updateUser()
  const onSubmit = async () => {
    await updateUser()
    toast.info('You have successfully updated your employment details', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }

  const [localForm, setLocalForm] = useState({
    positionTitle: intakeState.positionTitle || '',
    dateOfHire: intakeState.dateOfHire || '',
    dateOfPlannedRetirement: intakeState.dateOfPlannedRetirement || '',
  })
  const handleChange = (field) => (value) => {
    setLocalForm((prev) => ({ ...prev, [field]: value }))
  }

  const FIXED_HEIGHT = 24
  const [logoWidth, setLogoWidth] = useState(0) // default fallback

  const logoUrl = useMemo(
    () =>
      selectData.agency.find(({ value }) => value === intakeState?.agency) ||
      intakeState?.employerDetails?.logo ||
      '',
    [intakeState?.agency, intakeState?.employerDetails?.logo]
  )
  useEffect(() => {
    if (!logoUrl) return

    const img = new Image()
    img.onload = () => {
      const { naturalWidth, naturalHeight } = img
      const scaledWidth = (naturalWidth / naturalHeight) * FIXED_HEIGHT
      setLogoWidth(scaledWidth)
    }
    img.src = logoUrl
  }, [logoUrl])
  const agencyLogoStyle = useMemo(
    () => ({
      '& [class$="container"]>div:first-of-type>div:first-of-type>div:first-of-type':
        {
          display: 'flex',
        },
      '& [class$="container"]>div:first-of-type>div:first-of-type>div:first-of-type::before':
        {
          content: '""',
          display: 'inline-block',
          height: `${FIXED_HEIGHT}px`,
          width: `${logoWidth}px`,
          marginRight: '8px',
          marginTop: '2px',
          backgroundImage: `url("${logoUrl}")`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
        },
    }),
    [logoUrl, logoWidth]
  )

  return (
    <>
      <Box
        pl={3}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography display="flex" align="left" variant="body2FunHeader">
          EMPLOYMENT
        </Typography>

        <LoadingButton loading={loading} onClick={onSubmit} />
      </Box>
      <Card>
        <Box component="form" p={3}>
          <Grid container columnSpacing={3} rowSpacing={2}>
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                autoFocus: false,
                id: 'employmentStatus',
                label: 'Employment Status',
                placeholder: '',
                inputType: 'select',
                showAdornment: false,
                value: intakeState.employmentStatus,
                onChange: updateIntakeStateAndSubmit((employmentStatus) =>
                  updateIntakeStateAtKey({ employmentStatus })
                ),
                options: selectData.employmentStatus,
              })}
            </Grid>
            {isGovernmentEmployee ? (
              <Grid item xs={12} sm={6} sx={agencyLogoStyle}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'agency',
                  label: 'Federal Agency',
                  placeholder: '',
                  inputType: 'select',
                  showAdornment: false,
                  searchable: true,
                  value: intakeState.agency,
                  onChange: updateIntakeStateAndSubmit((agency) =>
                    updateIntakeStateAtKey({ agency })
                  ),
                  options: selectData.agency,
                })}
              </Grid>
            ) : (
              <Grid item xs={12} sm={6}>
                <CompanySearchAutocomplete
                  placeholder="Search for your employer..."
                  value={intakeState.employerDetails?.name || ''}
                  onChange={updateIntakeStateAndSubmit((value) =>
                    updateIntakeStateAtKey({ employerDetails: value })
                  )}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                showAdornment: false,
                autoFocus: false,
                id: 'positionTitle',
                label: 'Position/Title',
                placeholder: 'Position/Title',
                inputType: 'text',
                value: localForm.positionTitle,
                onChange: (e) => handleChange('positionTitle')(e),
                onBlur: updateIntakeStateAndSubmit((val) =>
                  updateIntakeStateAtKey({ positionTitle: val })
                ),
              })}
            </Grid>
            {isGovernmentEmployee && (
              <Grid item xs={12} sm={6}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'retirementSystem',
                  label: 'Retirement System',
                  placeholder: '',
                  inputType: 'select',
                  showAdornment: false,

                  value: intakeState.retirementSystem,
                  onChange: updateIntakeStateAndSubmit((retirementSystem) =>
                    updateIntakeStateAtKey({ retirementSystem })
                  ),
                  options: selectData.retirementSystem,
                })}
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                autoFocus: false,

                id: 'dateOfHire',
                label: (
                  <>
                    <span>
                      Service Computation Date
                      <span style={{ color: 'red' }}> *</span>
                      <Tooltip
                        TransitionComponent={Fade}
                        title="Your official date of hire with the government, including any bought back military time. If you don't know, put your best guess or check your latest SF-50"
                        placement="top"
                        color="info"
                      >
                        <IconButton size="small" color="dark">
                          <HelpIcon sx={{ marginTop: '-6px' }} />
                        </IconButton>
                      </Tooltip>
                    </span>
                  </>
                ),
                placeholder: '01/01/1970',
                inputType: 'date',
                value: localForm.dateOfHire,
                onChange: (e) => handleChange('dateOfHire')(e),
                onBlur: updateIntakeStateAndSubmit((dateOfHire) =>
                  updateIntakeStateAtKey({ dateOfHire })
                ),
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                autoFocus: false,
                id: 'dateOfPlannedRetirement',
                label: 'Planned Retirement Date',
                inputType: 'date',
                value: localForm.dateOfPlannedRetirement,
                onChange: (e) => handleChange('dateOfPlannedRetirement')(e),
                onBlur: updateIntakeStateAndSubmit((dateOfPlannedRetirement) =>
                  updateIntakeStateAtKey({ dateOfPlannedRetirement })
                ),
              })}
            </Grid>
          </Grid>
        </Box>
      </Card>
    </>
  )
}

export default React.memo(EmploymentDetails)
